/**
 * The settings page registry: one panel per URL section slug. The
 * `/$org/settings/$section` route renders from this record, so adding a
 * page means registering it here (the `SettingsSectionSlug` key type makes
 * missing catalog sections a compile error) — no new route file.
 *
 * @module components/settings/settingsSections
 */
import type { ComponentType } from "react";

import { AccountSettingsPanel } from "./AccountSettings";
import { BrandingSettingsPanel } from "./BrandingSettings";
import { DiagnosticsSettingsPanel } from "./DiagnosticsSettings";
import { KeybindingsSettingsPanel } from "./KeybindingsSettings";
import { OrganizationSettingsPanel } from "./OrganizationSettings";
import { MembersSettingsPanel } from "./MembersSettings";
import { UsageAllowancesSettingsPanel } from "./UsageAllowancesSettings";
import { YourContextSettingsPanel } from "./YourContextSettings";
import { SecuritySettingsPanel } from "./SecuritySettings";
import {
  AppearanceSettingsPanel,
  ArchivedThreadsPanel,
  GeneralSettingsPanel,
} from "./SettingsPanels";
import { settingsSectionSlug, type SettingsSectionSlug } from "./settingsPaths";
import { SETTINGS_SECTION_ALIASES } from "./settingsSearch";

/** Diagnostics is reachable but deliberately unlisted in the sidebar catalog. */
type RegisteredSection = SettingsSectionSlug | "diagnostics";

export const SETTINGS_SECTION_PANELS: Readonly<Record<RegisteredSection, ComponentType>> = {
  account: AccountSettingsPanel,
  "your-context": YourContextSettingsPanel,
  security: SecuritySettingsPanel,
  general: GeneralSettingsPanel,
  appearance: AppearanceSettingsPanel,
  keybindings: KeybindingsSettingsPanel,
  archived: ArchivedThreadsPanel,
  organization: OrganizationSettingsPanel,
  members: MembersSettingsPanel,
  usage: UsageAllowancesSettingsPanel,
  branding: BrandingSettingsPanel,
  diagnostics: DiagnosticsSettingsPanel,
};

/** The current slug for a section that moved ("team" → "members"), or null. */
export function canonicalSettingsSection(section: string): SettingsSectionSlug | null {
  // Own keys only: the slug comes from the URL, and "constructor" or
  // "toString" would otherwise read the object's prototype.
  return Object.hasOwn(SETTINGS_SECTION_ALIASES, section)
    ? settingsSectionSlug(SETTINGS_SECTION_ALIASES[section]!)
    : null;
}

export function settingsPanelForSection(section: string): ComponentType | null {
  return Object.hasOwn(SETTINGS_SECTION_PANELS, section)
    ? (SETTINGS_SECTION_PANELS as Readonly<Record<string, ComponentType>>)[section]!
    : null;
}
